import React from 'react';
import clsx from 'clsx';
import { XCircle } from 'lucide-react';
import Message from '@components/common/Message';
import type { Assessment } from '@app-types';
import { formatMediumDate } from '@utils/dateUtils';
import styles from './CancellationBanner.module.css';

interface CancellationBannerProps {
  assessment: Assessment;
}

/**
 * Who cancelled the assessment, when, and why. Renders nothing for an
 * assessment that is not cancelled, or for a legacy Cancelled row that has no
 * cancellation data (no backfill): the Cancelled badge is enough there.
 */
const CancellationBanner: React.FC<CancellationBannerProps> = ({ assessment }) => {
  const { cancellation_reason: reason, cancelled_at: cancelledAt, cancelled_by_name: cancelledBy } = assessment;
  const reasonRef = React.useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = React.useState(false);
  const [overflows, setOverflows] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = reasonRef.current;
    if (el && !expanded) setOverflows(el.scrollHeight > el.clientHeight);
  }, [reason, expanded]);

  if (assessment.assessment_status !== 'Cancelled') return null;
  if (!reason && !cancelledAt && !cancelledBy) return null;

  const when = cancelledAt ? ` on ${formatMediumDate(cancelledAt)}` : '';
  const who = cancelledBy ? ` by ${cancelledBy}` : '';

  return (
    <Message variant="warning" icon={<XCircle size={20} />} className={styles.banner} data-test-id="cancellation-banner">
      <div className={styles.content}>
        <p className={styles.summary}>
          This assessment was cancelled{when}
          {who}.
        </p>
        {reason && (
          <>
            <p
              ref={reasonRef}
              className={clsx(styles.reason, !expanded && styles.reasonClamped)}
              data-test-id="cancellation-reason"
            >
              {reason}
            </p>
            {(overflows || expanded) && (
              <button type="button" className={styles.toggle} onClick={() => setExpanded((v) => !v)}>
                {expanded ? 'Show less' : 'Show full reason'}
              </button>
            )}
          </>
        )}
      </div>
    </Message>
  );
};

export default CancellationBanner;
